import { useEffect, useMemo, useState } from "react";
import styles from "./CandidateDataTable.module.css";
import { SORTING_OPTIONS } from "../../../../constant";
import DataTableWithFilters from "../../../CommonComponents/DataTableWithFilters";
import { HiOutlineDotsVertical } from "react-icons/hi";
import ActionMenu from "../../../CommonComponents/ActionMenu";
import { CANDIDATE, ENTERPRISE_COMPANY } from "../../../../urlConfig";
import { formatNumbers } from "../../../../utils/NumberFormat";
import { useNavigate } from "react-router-dom";
import EnterpriseServices from "../../../../services/EnterpriseServices";
import useNotification from "../../../../hooks/useNotification";
import userAvatar from "../../../../assets/userAvatar-Other.png";
import CandidateServices from "../../../../services/CandidateServices";

const CandidateDataTable = () => {
  const [counts, setCounts] = useState(0);
  const navigate = useNavigate();
  const [selectedSort, setSelectedSort] = useState(["Most Recent"]);
  const [searchInput, setSearchInput] = useState("");
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [nextPage, setNextPage] = useState();
  const [prevPage, setPrevPage] = useState();
  const [total, setTotal] = useState(100);

  const fetchUserOverviewData = async (nextPage, prevPage) => {
    setLoading(true);

    let queryParams = [`pageSize=10`];

    if (nextPage) queryParams.push(`nextPage=${nextPage}`);
    if (prevPage) queryParams.push(`prevPage=${prevPage}`);
    if (searchInput) queryParams.push(`search=${searchInput}`);

    let str = queryParams.join("&");

    const res = await CandidateServices.getCandidateList(str);

    if (res) {
      if (res.statusCode === 1) {
        setData(res.data);
        setTotal(res.total);
        setNextPage(res.nextPage);
        setPrevPage(res.prevPage);
      } else {
        handleError(`${res.statusDesc}`);
      }
    } else {
      handleError(`Error: ${res.statusDesc}`);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUserOverviewData();
  }, []);

  const handleDelete = (id) => {
    console.warn("delete clicked", id);
  };

  const revokeAccess = async (data) => {
    console.log("id", data);
    if (data?._id) {
      try {
        setLoading(true);
        let res = await CandidateServices.revokeAccess(data?._id);
        if (res) {
          if (res.statusCode === 1) {
            fetchUserOverviewData();
            showNotification(
              `Candidate access ${!data.isActive ? "Re-initated" : "Revoked"}`
            );
          } else {
            handleError(`${res.statusDesc}`);
          }
        } else {
          handleError(`Error: ${res.statusDesc}`);
        }
        setLoading(false);
      } catch (error) {
        console.error(error);
        handleError(`Error: ${error.message}`);
      }
    }
  };

  const handleClick = (record) => {
    navigate(CANDIDATE + "/" + record._id);
  };

  const handleError = (error) => {
    setLoading(false);
    showNotification(error, "error");
  };

  const handleSearch = (value) => {
    setSearchInput(value);
  };

  const handlePageChange = (isNextPage) => {
    if (isNextPage) {
      fetchUserOverviewData(nextPage, "", false);
    } else {
      fetchUserOverviewData("", prevPage, true);
    }
  };

  useEffect(() => {
    fetchUserOverviewData();
  }, [searchInput]);

  const columns = [
    {
      title: `Candidate (${total})`,
      dataIndex: "companyName",
      key: "companyName",
      minWidth: 200,
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
      render: (name, record) => {
        return (
          <div
            className={styles.user_image_wrap}
            onClick={() => handleClick(record)}
          >
            <img
              src={
                record?.profileImage?.docs
                  ? record?.profileImage?.docs
                  : userAvatar
              }
              alt="user avatar"
            />
            {record.firstName + " " + record.lastName}
          </div>
        );
      },
    },
    {
      title: "Title",
      dataIndex: "title",
      minWidth: 120,
      key: "title",
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: "Email",
      dataIndex: "emailAddress",
      minWidth: 120,
      key: "emailAddress",
      sorter: (a, b) => a.emailAddress.localeCompare(b.emailAddress),
    },
    {
      title: "Sector",
      dataIndex: "sector",
      minWidth: 120,
      key: "sector",
      sorter: (a, b) => a.sector.localeCompare(b.sector),
    },
    {
      title: "Date Of Birth",
      dataIndex: "dob",
      minWidth: 120,
      key: "dob",
      sorter: (a, b) => new Date(b.dob) - new Date(a.dob),
    },
    {
      title: "Profile Progress",
      dataIndex: "progress",
      key: "progress",
      minWidth: 120,
      sorter: (a, b) => a.progress.total - b.progress.total,
      render: (progress, record) => {
        return <div className={styles.tag_cell}>{progress.total}</div>;
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      minWidth: 120,
      sorter: (a, b) => (a.isActive === b.isActive ? 0 : a.isActive ? 1 : -1),
      render: (status, record) => {
        return (
          <div
            className={`${styles.status_cell}
              }`}
          >
            {status ? "Active" : "Revoked"}
          </div>
        );
      },
    },
    {
      title: "",
      dataIndex: "Actions",
      key: "Actions",
      minWidth: 10,
      maxWidth: 15,
      render: (status, record) => {
        return (
          <div>
            <ActionMenu
              options={[
                {
                  label: "Edit",
                  route: ENTERPRISE_COMPANY + record.key,
                },
                {
                  label: !record.isActive
                    ? "Re-initate Access"
                    : "Revoke Access",
                  action: () => revokeAccess(record),
                },
              ]}
            />
          </div>
        );
      },
    },
  ];

  const handleSortChange = (value) => {
    setSelectedSort(value);
  };

  const handleFilterChange = (value) => { };

  return (
    <div className={styles.content_wrap}>
      <DataTableWithFilters
        columns={columns}
        data={data}
        setCounts={setCounts}
        sortOptions={SORTING_OPTIONS}
        handleFilterChange={handleFilterChange}
        handleSortChange={handleSortChange}
        selectedSort={selectedSort}
        handleSearch={handleSearch}
        tableKey={"key"}
        pageSize={10}
        loading={loading}
        total={total}
        handlePageChange={handlePageChange}
        scroll={{
          x: "max-content",
        }}
      />
    </div>
  );
};

export default CandidateDataTable;
